import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useWallet } from '@solana/wallet-adapter-react';
import { WalletMultiButton } from '@solana/wallet-adapter-react-ui';
import { authAPI, priceAPI, googleOAuthAPI } from '../services/api';
import { GoogleOAuthProvider, GoogleLogin } from '@react-oauth/google';
import { Helmet } from 'react-helmet';
import QuickOrderForm from '../components/QuickOrderForm';

// FAQ Item Component
const FAQItem = ({ question, answer }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="reaction-card hover:border-purple-500/50 transition-all">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between text-left p-6"
      >
        <span className="text-lg font-semibold pr-8">{question}</span>
        <svg
          className={`w-6 h-6 flex-shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {isOpen && (
        <div className="px-6 pb-6 text-gray-300 leading-relaxed animate-fadeIn">
          {answer}
        </div>
      )}
    </div>
  );
};

const LandingPage = ({ user, onLogin, onLogout }) => {
  const navigate = useNavigate();
  const { publicKey } = useWallet();
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [show2FAModal, setShow2FAModal] = useState(false);
  const [isLogin, setIsLogin] = useState(true);
  const [reactions, setReactions] = useState([]);
  const [solPrice, setSolPrice] = useState(null);
  const [twoFACode, setTwoFACode] = useState('');
  const [pendingAuth, setPendingAuth] = useState(null);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    full_name: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [googleOAuthEnabled, setGoogleOAuthEnabled] = useState(false);
  const [googleClientId, setGoogleClientId] = useState('');

  useEffect(() => {
    fetchReactions();
    fetchSolPrice();
    checkGoogleOAuthStatus();
  }, []);

  const checkGoogleOAuthStatus = async () => {
    try {
      const response = await googleOAuthAPI.getStatus();
      setGoogleOAuthEnabled(response.data.enabled);
      setGoogleClientId(response.data.client_id);
    } catch (error) {
      console.error('Error checking Google OAuth status:', error);
    }
  };

  const fetchReactions = async () => {
    try {
      const response = await priceAPI.getReactions();
      setReactions(response.data);
    } catch (error) {
      console.error('Error fetching reactions:', error);
    }
  };

  const fetchSolPrice = async () => {
    try {
      const response = await priceAPI.getSolPrice();
      setSolPrice(response.data.price_usd);
    } catch (error) {
      console.error('Error fetching SOL price:', error);
    }
  };

  const handleAuth = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = isLogin
        ? await authAPI.login({ email: formData.email, password: formData.password })
        : await authAPI.register(formData);

      // Check if user has 2FA enabled
      if (isLogin && response.data.user.two_fa_enabled) {
        setPendingAuth(response.data);
        setShowAuthModal(false);
        setShow2FAModal(true);
      } else {
        onLogin(response.data.token, response.data.user);
        setShowAuthModal(false);
        navigate('/dashboard');
      }
    } catch (error) {
      setError(error.response?.data?.detail || 'Authentication failed');
    } finally {
      setLoading(false);
    }
  };

  const handle2FAVerify = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await authAPI.verify2FA({
        email: formData.email,
        code: twoFACode
      });

      // 2FA verified, proceed with login
      onLogin(pendingAuth.token, pendingAuth.user);
      setShow2FAModal(false);
      setTwoFACode('');
      setPendingAuth(null);
      navigate('/dashboard');
    } catch (error) {
      console.error('2FA verification error:', error);
      // Handle different error formats
      let errorMessage = 'Invalid 2FA code';
      if (error.response?.data?.detail) {
        if (typeof error.response.data.detail === 'string') {
          errorMessage = error.response.data.detail;
        } else if (Array.isArray(error.response.data.detail)) {
          errorMessage = error.response.data.detail.map(e => e.msg || e.message).join(', ');
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = async (credentialResponse) => {
    setError('');
    setLoading(true);

    try {
      const response = await googleOAuthAPI.authenticate(credentialResponse.credential);
      
      // Login successful
      onLogin(response.data.token, response.data.user);
      setShowAuthModal(false);
      navigate('/dashboard');
    } catch (error) {
      console.error('Google login error:', error);
      let errorMessage = 'Google login failed';
      if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      }
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleError = () => {
    setError('Google login was cancelled or failed');
  };

  const packages = [
    { quantity: 500, popular: false },
    { quantity: 1000, popular: true },
    { quantity: 1500, popular: false },
    { quantity: 2000, popular: false }
  ];

  return (
    <>
      <Helmet>
        <title>Buy Dexscreener Reaction - #1 Dexmoji & Rockets Bot | Dexreaction</title>
        <meta name="description" content="Buy Dexscreener Reaction from #1 provider. Get Dexmoji rockets 🚀, fire 🔥 & star ⭐ reactions at $0.05 each. Fastest delivery, cheapest prices, 10% bonus. Telegram bot & web panel!" />
        <meta name="keywords" content="buy dexscreener reaction, buy dexscreener reactions, buy dexscreener rockets, buy dexscreener fire, buy dexscreener poop, dexreactions, dexscreener reaction, dexscreener reactions, dexscreener rockets, buy dexmoji, dexmoji bot, dexscreener reactions bot, telegram dexscreener bot, boost dexscreener trending, dexscreener emoji reactions, crypto token promotion, cheap dexscreener reactions" />
        <link rel="canonical" href="https://dexreaction.com/" />
        <meta property="og:title" content="Buy Dexscreener Reaction - Cheapest Dexmoji Bot | Dexreaction" />
        <meta property="og:description" content="#1 Dexscreener reaction service. Buy Dexmoji rockets, fire, star reactions. $0.05/reaction, instant delivery, Telegram bot available!" />
        <meta property="og:image" content="https://dexreaction.com/og-image.png" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://dexreaction.com/" />
        <meta property="og:image" content="https://dexreaction.com/logo-light.png" />
        <link rel="sitemap" type="application/xml" href="/sitemap.xml" />
      </Helmet>
      
      <div className="landing-hero">
        {/* Navbar */}
        <nav className="navbar fixed w-full top-0 z-50 px-4 md:px-6 py-3 md:py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <img src="/logo-light.png" alt="Dexreaction" className="h-8 md:h-10 w-auto object-contain" />
          </div>
          <div className="flex items-center space-x-2 md:space-x-4">
            {solPrice && (
              <div className="text-xs md:text-sm text-gray-300">
                SOL: ${solPrice.toFixed(2)}
              </div>
            )}
            {/* API Developer Link */}
            <button 
              onClick={() => navigate('/api-developer')} 
              className="text-gray-400 hover:text-purple-400 text-sm md:text-base transition-colors hidden md:inline-flex items-center"
            >
              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
              </svg>
              API
            </button>
            {user ? (
              <>
                <button onClick={() => navigate('/dashboard')} className="btn-secondary text-sm md:text-base px-3 py-1.5 md:px-4 md:py-2 hidden md:inline-block">
                  Dashboard
                </button>
                <button onClick={onLogout} className="btn-secondary text-sm md:text-base px-3 py-1.5 md:px-4 md:py-2 hidden md:inline-block">
                  Logout
                </button>
              </>
            ) : (
              <button onClick={() => setShowAuthModal(true)} className="btn-primary text-sm md:text-base px-4 py-2 md:px-6 md:py-3">
                Get Started
              </button>
            )}
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <div className="pt-32 pb-20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="fade-in">
              <h1 className="text-5xl md:text-6xl font-bold mb-6" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                Buy Dexscreener Reactions
                <br />
                <span className="bg-gradient-to-r from-purple-400 via-pink-400 to-blue-400 bg-clip-text text-transparent">
                  Rocket and Fire Instantly
                </span>
              </h1>
              <p className="text-xl text-gray-300 mb-8 leading-relaxed">
                Increase visibility and engagement for your token with authentic reactions. 
                Professional service, instant delivery, and secure Solana payments.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 mb-8">
                <button 
                  onClick={() => user ? navigate('/dashboard') : setShowAuthModal(true)} 
                  className="btn-primary text-lg px-10 py-4 flex items-center justify-center"
                >
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                  Start Boosting Now
                </button>
                <button 
                  onClick={() => {
                    window.open('https://t.me/dexreactionrobot', '_blank');
                  }}
                  className="btn-secondary text-lg px-10 py-4 flex items-center justify-center"
                >
                  <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm5.562 8.161c-.18 1.897-.962 6.502-1.359 8.627-.168.9-.5 1.201-.82 1.23-.697.064-1.226-.461-1.901-.903-1.056-.693-1.653-1.124-2.678-1.799-1.185-.781-.417-1.21.258-1.911.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.139-5.062 3.345-.479.329-.913.489-1.302.481-.428-.009-1.252-.242-1.865-.442-.751-.244-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.831-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635.099-.002.321.023.465.14.121.098.155.23.171.324.016.094.036.308.02.475z"/>
                  </svg>
                  Try Telegram Bot
                </button>
              </div>
              <div className="flex items-center space-x-8 text-sm text-gray-400">
                <div className="flex items-center">
                  <svg className="w-5 h-5 mr-2 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  Instant Delivery
                </div>
                <div className="flex items-center">
                  <svg className="w-5 h-5 mr-2 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  Secure Payment
                </div>
                <div className="flex items-center">
                  <svg className="w-5 h-5 mr-2 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  24/7 Support
                </div>
              </div>
            </div>
            <div className="relative fade-in">
              <div className="relative z-10">
                <QuickOrderForm />
              </div>
              <div className="absolute -top-4 -right-4 w-72 h-72 bg-purple-500/20 rounded-full blur-3xl animate-pulse"></div>
              <div className="absolute -bottom-4 -left-4 w-72 h-72 bg-blue-500/20 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
            </div>
          </div>
        </div>
      </div>

      {/* Reactions Section */}
      <div className="py-20 px-6">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-4xl font-bold text-center mb-12" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            Available Reactions
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-6">
            {reactions.map((reaction) => (
              <div key={reaction.id} className="reaction-card text-center" data-testid={`reaction-${reaction.id}`}>
                <div className="text-6xl mb-4">{reaction.emoji}</div>
                <h3 className="text-xl font-semibold mb-2">{reaction.name}</h3>
                <p className="text-purple-400 text-2xl font-bold">${reaction.price_usd}</p>
                <p className="text-gray-400 text-sm">per reaction</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Packages Section */}
      <div className="py-20 px-6">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-4xl font-bold text-center mb-12" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            Popular Packages
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {packages.map((pkg) => (
              <div key={pkg.quantity} className={`reaction-card relative ${pkg.popular ? 'border-purple-500' : ''}`}>
                {/* Bonus Badge */}
                <div className="absolute top-4 right-4 bg-gradient-to-r from-green-500 to-emerald-500 text-white text-xs font-bold px-3 py-1 rounded-full shadow-lg">
                  +10% Bonus
                </div>
                
                {pkg.popular && (
                  <div className="bg-purple-500 text-white text-xs font-bold px-3 py-1 rounded-full inline-block mb-4">
                    POPULAR
                  </div>
                )}
                <div className="text-center">
                  <div className="text-5xl font-bold mb-2">{pkg.quantity}</div>
                  <p className="text-gray-400 mb-1">Reactions</p>
                  <p className="text-green-400 text-sm font-semibold mb-4">
                    Get {pkg.quantity + Math.floor(pkg.quantity * 0.1)} total!
                  </p>
                  <div className="text-3xl font-bold text-purple-400 mb-4">
                    ${(pkg.quantity * 0.05).toFixed(2)}
                  </div>
                  <button onClick={() => user ? navigate('/dashboard') : setShowAuthModal(true)} className="btn-primary w-full">
                    Select
                  </button>
                </div>
              </div>
            ))}
          </div>
          <div className="text-center mt-8">
            <p className="text-gray-300 text-lg">
              Need a custom amount? Choose from 500 to 10,000 reactions in the dashboard.
            </p>
          </div>
        </div>
      </div>

      {/* Features Section */}
      <div id="features-section" className="py-20 px-6 bg-gradient-to-b from-transparent to-purple-900/10">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Why Choose Dexreaction?
            </h2>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto">
              The most trusted platform for boosting your token's social proof
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
            <div className="reaction-card text-center group hover:scale-105 transition-transform">
              <div className="w-16 h-16 mx-auto mb-4 bg-gradient-to-br from-purple-500/20 to-blue-500/20 rounded-2xl flex items-center justify-center">
                <svg className="w-8 h-8 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <h3 className="text-2xl font-semibold mb-3">Instant Processing</h3>
              <p className="text-gray-300">Orders processed immediately after payment confirmation. No waiting time.</p>
            </div>
            
            <div className="reaction-card text-center group hover:scale-105 transition-transform">
              <div className="w-16 h-16 mx-auto mb-4 bg-gradient-to-br from-green-500/20 to-emerald-500/20 rounded-2xl flex items-center justify-center">
                <svg className="w-8 h-8 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
              </div>
              <h3 className="text-2xl font-semibold mb-3">Secure Payments</h3>
              <p className="text-gray-300">All transactions verified on Solana blockchain for maximum security.</p>
            </div>
            
            <div className="reaction-card text-center group hover:scale-105 transition-transform">
              <div className="w-16 h-16 mx-auto mb-4 bg-gradient-to-br from-pink-500/20 to-rose-500/20 rounded-2xl flex items-center justify-center">
                <svg className="w-8 h-8 text-pink-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-2xl font-semibold mb-3">Best Prices</h3>
              <p className="text-gray-300">Only $0.05 per reaction with flexible quantity options from 1 to 10,000.</p>
            </div>
            
            <div className="reaction-card text-center group hover:scale-105 transition-transform">
              <div className="w-16 h-16 mx-auto mb-4 bg-gradient-to-br from-blue-500/20 to-cyan-500/20 rounded-2xl flex items-center justify-center">
                <svg className="w-8 h-8 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
              </div>
              <h3 className="text-2xl font-semibold mb-3">24/7 Support</h3>
              <p className="text-gray-300">Live support available anytime. Get help whenever you need it.</p>
            </div>
            
            <div className="reaction-card text-center group hover:scale-105 transition-transform">
              <div className="w-16 h-16 mx-auto mb-4 bg-gradient-to-br from-yellow-500/20 to-orange-500/20 rounded-2xl flex items-center justify-center">
                <svg className="w-8 h-8 text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z" />
                </svg>
              </div>
              <h3 className="text-2xl font-semibold mb-3">Support Tickets</h3>
              <p className="text-gray-300">Create tickets for any order. Track issues and get personalized support.</p>
            </div>
            
            <div className="reaction-card text-center group hover:scale-105 transition-transform">
              <div className="w-16 h-16 mx-auto mb-4 bg-gradient-to-br from-indigo-500/20 to-purple-500/20 rounded-2xl flex items-center justify-center">
                <svg className="w-8 h-8 text-indigo-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              </div>
              <h3 className="text-2xl font-semibold mb-3">Flexible Options</h3>
              <p className="text-gray-300">Choose automatic wallet payment or manual transfer with verification.</p>
            </div>
          </div>

          {/* Ranking Benefits Section */}
          <div className="mt-20">
            <div className="text-center mb-12">
              <h2 className="text-4xl md:text-5xl font-bold mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                Why DexScreener Reactions Matter
              </h2>
              <p className="text-xl text-gray-400 max-w-3xl mx-auto">
                Boost your token's ranking on DexScreener with authentic reactions that drive real visibility
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
              {/* Boost 40% */}
              <div className="reaction-card text-center group hover:scale-105 transition-transform">
                <div className="w-20 h-20 mx-auto mb-6 bg-gradient-to-br from-purple-500/20 to-blue-500/20 rounded-full flex items-center justify-center">
                  <div className="text-4xl">🚀</div>
                </div>
                <div className="text-5xl font-bold text-purple-400 mb-3">40%</div>
                <h3 className="text-2xl font-semibold mb-3">BOOST Impact</h3>
                <p className="text-gray-300">
                  Reactions significantly boost your token's visibility score, pushing it to the top trending sections of DexScreener
                </p>
              </div>

              {/* Volume 30% */}
              <div className="reaction-card text-center group hover:scale-105 transition-transform">
                <div className="w-20 h-20 mx-auto mb-6 bg-gradient-to-br from-green-500/20 to-emerald-500/20 rounded-full flex items-center justify-center">
                  <div className="text-4xl">📈</div>
                </div>
                <div className="text-5xl font-bold text-green-400 mb-3">30%</div>
                <h3 className="text-2xl font-semibold mb-3">VOLUME Influence</h3>
                <p className="text-gray-300">
                  Higher reaction counts correlate with increased trading volume perception, attracting more traders to your token
                </p>
              </div>

              {/* Reaction 30% */}
              <div className="reaction-card text-center group hover:scale-105 transition-transform">
                <div className="w-20 h-20 mx-auto mb-6 bg-gradient-to-br from-pink-500/20 to-rose-500/20 rounded-full flex items-center justify-center">
                  <div className="text-4xl">⭐</div>
                </div>
                <div className="text-5xl font-bold text-pink-400 mb-3">30%</div>
                <h3 className="text-2xl font-semibold mb-3">REACTION Power</h3>
                <p className="text-gray-300">
                  Authentic reactions build social proof and credibility, making your token stand out in a crowded marketplace
                </p>
              </div>
            </div>

            {/* Formula */}
            <div className="max-w-4xl mx-auto">
              <div className="reaction-card bg-gradient-to-br from-purple-500/10 to-blue-500/10 border-2 border-purple-500/30">
                <div className="text-center mb-6">
                  <h3 className="text-2xl font-bold mb-4">DexScreener Ranking Formula</h3>
                  <div className="text-lg text-gray-300 mb-6">
                    Your token's position is determined by a combination of factors:
                  </div>
                </div>
                
                <div className="flex flex-wrap justify-center items-center gap-4 text-xl font-semibold">
                  <div className="px-6 py-3 bg-purple-500/20 rounded-lg border border-purple-500/30">
                    <span className="text-purple-400">40%</span> BOOST
                  </div>
                  <span className="text-gray-400">+</span>
                  <div className="px-6 py-3 bg-green-500/20 rounded-lg border border-green-500/30">
                    <span className="text-green-400">30%</span> VOLUME
                  </div>
                  <span className="text-gray-400">+</span>
                  <div className="px-6 py-3 bg-pink-500/20 rounded-lg border border-pink-500/30">
                    <span className="text-pink-400">30%</span> REACTIONS
                  </div>
                  <span className="text-gray-400">=</span>
                  <div className="px-6 py-3 bg-yellow-500/20 rounded-lg border border-yellow-500/30">
                    <span className="text-yellow-400">🏆</span> TOP RANKING
                  </div>
                </div>

                <div className="mt-8 text-center">
                  <p className="text-gray-300 mb-4">
                    <strong className="text-white">Maximize all three factors</strong> to achieve the highest rankings and gain maximum exposure for your token on DexScreener's main page and trending lists.
                  </p>
                  <button 
                    onClick={() => user ? navigate('/dashboard') : setShowAuthModal(true)}
                    className="btn-primary text-lg px-10 py-4"
                  >
                    Start Boosting Your Ranking
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Telegram Bot Section */}
          <div className="mt-20">
            <div className="max-w-6xl mx-auto">
              <div className="reaction-card bg-gradient-to-br from-blue-500/10 to-cyan-500/10 border-2 border-blue-500/30">
                <div className="grid md:grid-cols-2 gap-8 items-center">
                  {/* Left Side - Content */}
                  <div>
                    <div className="flex items-center mb-4">
                      <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-xl flex items-center justify-center mr-3">
                        <svg className="w-7 h-7 text-white" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm5.562 8.161c-.18 1.897-.962 6.502-1.359 8.627-.168.9-.5 1.201-.82 1.23-.697.064-1.226-.461-1.901-.903-1.056-.693-1.653-1.124-2.678-1.799-1.185-.781-.417-1.21.258-1.911.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.139-5.062 3.345-.479.329-.913.489-1.302.481-.428-.009-1.252-.242-1.865-.442-.751-.244-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.831-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635.099-.002.321.023.465.14.121.098.155.23.171.324.016.094.036.308.02.475z"/>
                        </svg>
                      </div>
                      <h3 className="text-3xl font-bold">Boost via Telegram Bot</h3>
                    </div>
                    <p className="text-xl text-gray-300 mb-6">
                      Experience the easiest way to boost your token's visibility. Our Telegram bot provides a seamless, chat-based interface for all your boosting needs.
                    </p>
                    
                    <div className="space-y-4 mb-8">
                      <div className="flex items-start">
                        <div className="w-8 h-8 bg-green-500/20 rounded-lg flex items-center justify-center mr-3 mt-1 flex-shrink-0">
                          <svg className="w-5 h-5 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                        </div>
                        <div>
                          <h4 className="font-semibold text-white mb-1">Instant Ordering</h4>
                          <p className="text-gray-400 text-sm">Place orders in seconds directly through Telegram chat - no website login required</p>
                        </div>
                      </div>
                      
                      <div className="flex items-start">
                        <div className="w-8 h-8 bg-green-500/20 rounded-lg flex items-center justify-center mr-3 mt-1 flex-shrink-0">
                          <svg className="w-5 h-5 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                        </div>
                        <div>
                          <h4 className="font-semibold text-white mb-1">Real-Time Updates</h4>
                          <p className="text-gray-400 text-sm">Get instant notifications about your order status and balance changes</p>
                        </div>
                      </div>
                      
                      <div className="flex items-start">
                        <div className="w-8 h-8 bg-green-500/20 rounded-lg flex items-center justify-center mr-3 mt-1 flex-shrink-0">
                          <svg className="w-5 h-5 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                        </div>
                        <div>
                          <h4 className="font-semibold text-white mb-1">Easy Balance Management</h4>
                          <p className="text-gray-400 text-sm">Top-up your balance and pay with in-app balance for faster transactions</p>
                        </div>
                      </div>
                      
                      <div className="flex items-start">
                        <div className="w-8 h-8 bg-green-500/20 rounded-lg flex items-center justify-center mr-3 mt-1 flex-shrink-0">
                          <svg className="w-5 h-5 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                        </div>
                        <div>
                          <h4 className="font-semibold text-white mb-1">Order History & Support</h4>
                          <p className="text-gray-400 text-sm">View your complete order history and get instant support - all within Telegram</p>
                        </div>
                      </div>
                    </div>
                    
                    <button 
                      onClick={() => window.open('https://t.me/dexreactionrobot', '_blank')}
                      className="btn-primary text-lg px-8 py-4 w-full md:w-auto flex items-center justify-center"
                    >
                      <svg className="w-6 h-6 mr-2" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm5.562 8.161c-.18 1.897-.962 6.502-1.359 8.627-.168.9-.5 1.201-.82 1.23-.697.064-1.226-.461-1.901-.903-1.056-.693-1.653-1.124-2.678-1.799-1.185-.781-.417-1.21.258-1.911.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.139-5.062 3.345-.479.329-.913.489-1.302.481-.428-.009-1.252-.242-1.865-.442-.751-.244-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.831-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635.099-.002.321.023.465.14.121.098.155.23.171.324.016.094.036.308.02.475z"/>
                      </svg>
                      Open Telegram Bot
                    </button>
                  </div>
                  
                  {/* Right Side - Visual/Screenshot */}
                  <div className="relative">
                    <div className="bg-slate-800/50 rounded-2xl p-6 border border-slate-700">
                      <div className="bg-slate-900 rounded-lg p-4 space-y-3">
                        {/* Mock Telegram Chat Messages */}
                        <div className="flex items-start space-x-3">
                          <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-full flex items-center justify-center flex-shrink-0">
                            <span className="text-white font-bold">DX</span>
                          </div>
                          <div className="bg-blue-500/20 rounded-2xl rounded-tl-none px-4 py-3 max-w-[80%]">
                            <p className="text-sm text-gray-200">Welcome to Dexreaction! 🚀</p>
                            <p className="text-xs text-gray-400 mt-1">What would you like to do?</p>
                          </div>
                        </div>
                        
                        <div className="flex justify-end">
                          <div className="bg-purple-500/20 rounded-2xl rounded-tr-none px-4 py-3 max-w-[80%]">
                            <p className="text-sm text-gray-200">Create new order</p>
                          </div>
                        </div>
                        
                        <div className="flex items-start space-x-3">
                          <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-full flex items-center justify-center flex-shrink-0">
                            <span className="text-white font-bold">DX</span>
                          </div>
                          <div className="bg-blue-500/20 rounded-2xl rounded-tl-none px-4 py-3 max-w-[80%]">
                            <p className="text-sm text-gray-200">Select reaction type:</p>
                            <div className="flex space-x-2 mt-2">
                              <div className="bg-slate-800 rounded-lg px-3 py-2 text-xl">🚀</div>
                              <div className="bg-slate-800 rounded-lg px-3 py-2 text-xl">🔥</div>
                              <div className="bg-slate-800 rounded-lg px-3 py-2 text-xl">💩</div>
                              <div className="bg-slate-800 rounded-lg px-3 py-2 text-xl">🚩</div>
                              <div className="bg-slate-800 rounded-lg px-3 py-2 text-xl">⭐️</div>
                            </div>
                          </div>
                        </div>
                        
                        <div className="text-center py-2">
                          <span className="text-xs text-gray-500">Fast & Easy Ordering</span>
                        </div>
                      </div>
                    </div>
                    
                    {/* Floating badge */}
                    <div className="absolute -top-4 -right-4 bg-gradient-to-br from-green-500 to-emerald-500 text-white px-4 py-2 rounded-full font-bold text-sm shadow-lg">
                      Available 24/7
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Testimonials Section */}
          <div className="mt-20">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                Trusted by Token Creators
              </h2>
              <p className="text-xl text-gray-400 max-w-2xl mx-auto">
                Join thousands of satisfied customers who boosted their token's visibility
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
              <div className="reaction-card text-center">
                <div className="flex justify-center mb-4">
                  {[...Array(5)].map((_, i) => (
                    <svg key={i} className="w-5 h-5 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                  ))}
                </div>
                <p className="text-gray-300 mb-4 italic">
                  "Incredible service! My token got 2000 reactions within minutes. The boost in visibility was immediate and helped attract real investors."
                </p>
                <div className="text-sm">
                  <p className="font-semibold text-purple-400">@CryptoBuilder</p>
                  <p className="text-gray-500">Token Creator</p>
                </div>
              </div>
              
              <div className="reaction-card text-center">
                <div className="flex justify-center mb-4">
                  {[...Array(5)].map((_, i) => (
                    <svg key={i} className="w-5 h-5 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                  ))}
                </div>
                <p className="text-gray-300 mb-4 italic">
                  "Fast, reliable, and affordable. Used Dexreaction for 3 different tokens and always got great results. Customer support is top-notch!"
                </p>
                <div className="text-sm">
                  <p className="font-semibold text-purple-400">@DeFiLauncher</p>
                  <p className="text-gray-500">Project Manager</p>
                </div>
              </div>
              
              <div className="reaction-card text-center">
                <div className="flex justify-center mb-4">
                  {[...Array(5)].map((_, i) => (
                    <svg key={i} className="w-5 h-5 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                  ))}
                </div>
                <p className="text-gray-300 mb-4 italic">
                  "The social proof from reactions helped my token gain credibility. Saw a 300% increase in organic engagement after using Dexreaction."
                </p>
                <div className="text-sm">
                  <p className="font-semibold text-purple-400">@TokenInnovator</p>
                  <p className="text-gray-500">Blockchain Developer</p>
                </div>
              </div>
            </div>
          </div>

          {/* FAQ Section */}
          <div className="mt-20">
            <div className="text-center mb-12">
              <h2 className="text-4xl md:text-5xl font-bold mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                Frequently Asked Questions
              </h2>
              <p className="text-xl text-gray-400">
                Everything you need to know about our service
              </p>
            </div>

            <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* Column 1 */}
              <div className="space-y-4">
                <FAQItem 
                  question="How fast will my order be processed?"
                  answer="Orders are processed instantly after payment confirmation. For automatic wallet payments, reactions are added within minutes. Manual transfers are verified by our admin team within a few hours."
                />
                <FAQItem 
                  question="What payment methods do you accept?"
                  answer="We accept Solana (SOL) payments through two methods: Automatic wallet connection for instant payment, or manual transfer where you send SOL directly to our wallet address and submit the transaction ID."
                />
                <FAQItem 
                  question="Is it safe to use this service?"
                  answer="Yes, absolutely! All transactions are verified on the Solana blockchain. We never ask for your private keys or sensitive information. Your payment is secure and transparent."
                />
                <FAQItem 
                  question="What if I have issues with my order?"
                  answer="We provide 24/7 support through our live chat and ticket system. You can create a support ticket directly from your order details, and our team will respond promptly to resolve any issues."
                />
              </div>
              
              {/* Column 2 */}
              <div className="space-y-4">
                <FAQItem 
                  question="Can I choose specific reaction types?"
                  answer="Yes! You can choose from 5 reaction types: Rocket 🚀, Fire 🔥, Poop 💩, Red Flag 🚩, and Favorite ⭐. Each reaction costs $0.05 regardless of type."
                />
                <FAQItem 
                  question="What's the minimum and maximum order?"
                  answer="You can order from 1 to 10,000 reactions in a single order. Whether you need just a few reactions to boost credibility or thousands for maximum impact, we've got you covered."
                />
                <FAQItem 
                  question="How do I track my order status?"
                  answer="After placing an order, you can track its status in your dashboard. Orders go through stages: Pending → Processing → Completed. You'll receive real-time updates via our notification system."
                />
                <FAQItem 
                  question="What happens if payment verification fails?"
                  answer="If your payment verification fails, you can contact support through the ticket system. Our admin team will manually review your transaction and resolve any issues quickly."
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="py-12 px-6 border-t border-purple-500/20">
        <div className="max-w-7xl mx-auto">
          {/* SEO-Rich Content Section */}
          <div className="mb-8 p-6 bg-slate-800/30 rounded-lg border border-purple-500/10">
            <h3 className="text-xl font-bold mb-4 text-center text-purple-400">
              Buy Dexscreener Reactions - Premium Dexmoji Service
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-sm text-gray-400">
              <div>
                <h4 className="font-semibold text-white mb-2">🚀 Rocket Dexscreener</h4>
                <p>
                  Boost your token visibility with <a href="/buy-dexscreener-rockets" className="text-purple-400 hover:text-purple-300">Buy Dexscreener Rockets</a>. 
                  Also check <a href="/buydexrockets" className="text-purple-400 hover:text-purple-300">BuyDexRockets</a> for rockets! 
                  Fast delivery and authentic engagement.
                </p>
              </div>
              <div>
                <h4 className="font-semibold text-white mb-2">🔥 Fire Dexscreener</h4>
                <p>
                  <a href="/buy-dexscreener-fire" className="text-purple-400 hover:text-purple-300">Buy Dexscreener Fire</a> reactions show your token is heating up! 
                  Buy <a href="/buy-dexmoji" className="text-purple-400 hover:text-purple-300">Dexmoji</a> to increase social proof and attract investors. 
                  Secure Solana payments accepted.
                </p>
              </div>
              <div>
                <h4 className="font-semibold text-white mb-2">💩 Poop & All Reactions</h4>
                <p>
                  <a href="/buy-dexscreener-poop" className="text-purple-400 hover:text-purple-300">Buy Dexscreener Poop</a> for meme tokens! 
                  Complete <a href="/dexreactions" className="text-purple-400 hover:text-purple-300">Dexreactions</a> service with 24/7 support. 
                  <a href="/cheap-dexscreener-reactions" className="text-purple-400 hover:text-purple-300">Cheap Dexscreener Reactions</a> from $0.05.
                </p>
              </div>
            </div>
            <div className="mt-6 pt-4 border-t border-purple-500/10 text-center">
              <p className="text-xs text-gray-500 leading-relaxed">
                <strong className="text-gray-400">Keywords:</strong> BuyDexRockets, Buy Dex Rockets, Buy Dexscreener Rockets, Buy Dexscreener Fire, Buy Dexscreener Poop, 
                Dexreactions, Cheap Dexscreener Reactions, Dexscreener Reactions, Buy Dexmoji, Emoji Dexscreener, 
                Rocket Dexscreener, Fire Dexscreener, DexScreener boost, crypto reactions, token promotion, 
                DexScreener ranking, buy crypto reactions, authentic Dexmoji, DexScreener emojis, token visibility, 
                social proof crypto, Solana payments, instant delivery
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
            {/* Brand */}
            <div className="text-center md:text-left">
              <h3 className="text-2xl font-bold mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                Dexreaction
              </h3>
              <p className="text-gray-400 text-sm">
                Premium Dexscreener Reactions provider - Buy Dexmoji for your token's success
              </p>
            </div>
            
            {/* Contact & Social */}
            <div className="text-center md:text-right">
              <h4 className="text-lg font-semibold mb-3">Connect With Us</h4>
              <div className="flex justify-center md:justify-end space-x-4 mb-4">
                {/* X (Twitter) */}
                <a 
                  href="https://x.com/dexreaction" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="w-10 h-10 bg-slate-800 hover:bg-purple-500/20 border border-purple-500/20 hover:border-purple-500/50 rounded-lg flex items-center justify-center transition-all group"
                  aria-label="Follow us on X"
                >
                  <svg className="w-5 h-5 text-gray-400 group-hover:text-purple-400 transition-colors" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                  </svg>
                </a>
                
                {/* Telegram */}
                <a 
                  href="https://t.me/dexreactionrobot" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="w-10 h-10 bg-slate-800 hover:bg-blue-500/20 border border-purple-500/20 hover:border-blue-500/50 rounded-lg flex items-center justify-center transition-all group"
                  aria-label="Join our Telegram"
                >
                  <svg className="w-5 h-5 text-gray-400 group-hover:text-blue-400 transition-colors" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm5.562 8.161c-.18 1.897-.962 6.502-1.359 8.627-.168.9-.5 1.201-.82 1.23-.697.064-1.226-.461-1.901-.903-1.056-.693-1.653-1.124-2.678-1.8-1.185-.781-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.139-5.062 3.345-.479.329-.913.489-1.302.481-.428-.008-1.252-.241-1.865-.44-.752-.244-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.831-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635.099-.002.321.023.465.14.122.099.155.232.171.326.016.093.036.306.02.472z"/>
                  </svg>
                </a>
                
                {/* Email */}
                <a 
                  href="mailto:info@dexreaction.com"
                  className="w-10 h-10 bg-slate-800 hover:bg-green-500/20 border border-purple-500/20 hover:border-green-500/50 rounded-lg flex items-center justify-center transition-all group"
                  aria-label="Email us"
                >
                  <svg className="w-5 h-5 text-gray-400 group-hover:text-green-400 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                </a>
              </div>
              <p className="text-gray-400 text-sm">
                <a href="mailto:info@dexreaction.com" className="hover:text-purple-400 transition-colors">
                  info@dexreaction.com
                </a>
              </p>
            </div>
          </div>
          
          {/* Bottom Bar */}
          <div className="pt-8 border-t border-purple-500/10 text-center text-gray-400">
            {/* Legal Links */}
            <div className="flex flex-wrap justify-center gap-4 mb-4">
              <a 
                href="/api-developer" 
                className="text-sm text-gray-400 hover:text-purple-400 transition-colors"
              >
                API Developer
              </a>
              <span className="text-gray-600">•</span>
              <a 
                href="/terms" 
                className="text-sm text-gray-400 hover:text-purple-400 transition-colors"
              >
                Terms & Conditions
              </a>
              <span className="text-gray-600">•</span>
              <a 
                href="/privacy" 
                className="text-sm text-gray-400 hover:text-purple-400 transition-colors"
              >
                Privacy Policy
              </a>
              <span className="text-gray-600">•</span>
              <a 
                href="/license" 
                className="text-sm text-gray-400 hover:text-purple-400 transition-colors"
              >
                License
              </a>
            </div>
            
            <p className="text-sm">&copy; 2025 Dexreaction. All rights reserved.</p>
            <p className="mt-2 text-xs flex items-center justify-center">
              <span>Powered by</span>
              <svg className="w-4 h-4 mx-1 text-purple-400" fill="currentColor" viewBox="0 0 24 24">
                <path d="M20.45 7.41l-8.04-5.67c-.27-.19-.63-.19-.9 0L3.55 7.41c-.27.19-.45.51-.45.85v7.48c0 .34.18.66.45.85l8.04 5.67c.27.19.63.19.9 0l8.04-5.67c.27-.19.45-.51.45-.85V8.26c0-.34-.18-.66-.45-.85z"/>
              </svg>
              <span className="text-purple-400 font-semibold">Solana Blockchain</span>
            </p>
          </div>
        </div>
      </footer>

      {/* Auth Modal */}
      {showAuthModal && (
        <div className="modal" onClick={() => setShowAuthModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} data-testid="auth-modal">
            <h2 className="text-3xl font-bold mb-6">{isLogin ? 'Login' : 'Create Account'}</h2>
            
            {/* Google Login Button */}
            {googleOAuthEnabled && googleClientId && (
              <div className="mb-6">
                <GoogleOAuthProvider clientId={googleClientId}>
                  <div className="flex justify-center">
                    <GoogleLogin
                      onSuccess={handleGoogleLogin}
                      onError={handleGoogleError}
                      useOneTap={false}
                      text={isLogin ? "signin_with" : "signup_with"}
                      theme="filled_blue"
                      size="large"
                      width="350"
                    />
                  </div>
                </GoogleOAuthProvider>
                
                <div className="flex items-center my-6">
                  <div className="flex-1 border-t border-gray-600"></div>
                  <span className="px-4 text-gray-400 text-sm">OR</span>
                  <div className="flex-1 border-t border-gray-600"></div>
                </div>
              </div>
            )}
            
            <form onSubmit={handleAuth} className="space-y-4">
              {!isLogin && (
                <input
                  type="text"
                  placeholder="Full Name"
                  className="input-field"
                  value={formData.full_name}
                  onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                  required
                  data-testid="input-fullname"
                />
              )}
              <input
                type="email"
                placeholder="Email"
                className="input-field"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
                data-testid="input-email"
              />
              <input
                type="password"
                placeholder="Password"
                className="input-field"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                required
                minLength={6}
                data-testid="input-password"
              />
              {error && <div className="text-red-400 text-sm">{error}</div>}
              <button type="submit" className="btn-primary w-full" disabled={loading} data-testid="btn-auth-submit">
                {loading ? 'Processing...' : (isLogin ? 'Login' : 'Register')}
              </button>
            </form>
            <div className="mt-6 text-center">
              <button
                onClick={() => {
                  setIsLogin(!isLogin);
                  setError('');
                }}
                className="text-purple-400 hover:text-purple-300"
              >
                {isLogin ? "Don't have an account? Register" : 'Already have an account? Login'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 2FA Modal */}
      {show2FAModal && (
        <div className="modal" onClick={() => setShow2FAModal(false)}>
          <div className="modal-content max-w-md" onClick={(e) => e.stopPropagation()}>
            <h2 className="text-3xl font-bold mb-6">Two-Factor Authentication</h2>
            <p className="text-gray-300 mb-6">Enter the 6-digit code from your authenticator app</p>
            <form onSubmit={handle2FAVerify} className="space-y-4">
              <input
                type="text"
                placeholder="000000"
                className="input-field text-center text-2xl font-mono tracking-widest"
                value={twoFACode}
                onChange={(e) => setTwoFACode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                required
                maxLength={6}
                autoFocus
              />
              {error && <div className="text-red-400 text-sm">{error}</div>}
              <button type="submit" className="btn-primary w-full" disabled={loading || twoFACode.length !== 6}>
                {loading ? 'Verifying...' : 'Verify & Login'}
              </button>
              <button
                type="button"
                onClick={() => {
                  setShow2FAModal(false);
                  setTwoFACode('');
                  setPendingAuth(null);
                }}
                className="btn-secondary w-full"
              >
                Cancel
              </button>
            </form>
          </div>
        </div>
      )}
      </div>
    </>
  );
};

export default LandingPage;
